import React, { useEffect, useState } from "react";
import {
  Box,
  Grid,
  InputLabel,
  TextField,
  Typography,
  Paper,
  Divider,
  Button,
  Snackbar,
  Alert,
} from "@mui/material";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { useTranslation } from "react-i18next";
import { useForm, Controller, useWatch } from "react-hook-form";
import CreateShareholder from "./CreateShareholder";
import { useSelector } from "react-redux";

function CreateCompany() {
  const { t } = useTranslation("Company");
  const userState = useSelector((state) => state.user);

  const { control, handleSubmit } = useForm({
    defaultValues: {
      NoAccpac: null,
      nom: null,
      businessName: null, // ✅ Nom commercial
      dateOuverture: null,
      dateFermeture: null,
      NoMarchand: null,
      noMarchandDC: null,
      passwordDC: null,
      escomptePaiementDetailFr: null,
      nomEmplacement: null,
      accessCodeMEV: null,
      nomExploitant: null,
      PST: null,
      GST_HST: null,
      shareholders: [],
    },
  });

  const [showSuccess, setShowSuccess] = useState(false);
  const [shareholders, setShareholders] = useState([]);

  const watchShareholders = useWatch({
    control,
    name: "shareholders",
  });

  useEffect(() => {
    if (watchShareholders) {
      setShareholders(watchShareholders);
    }
  }, [watchShareholders]);

  const onSubmit = async (formData) => {
    const dataCompany = {
      shareholders,
      NoAccpac: Number(formData.NoAccpac),
      nom: formData.nom,
      businessName: formData.businessName, // ✅ envoyé au backend
      dateOuverture: formData.dateOuverture,
      NoMarchand: formData.NoMarchand,
      noMarchandDC: formData.noMarchandDC,
      passwordDC: formData.passwordDC,
      escomptePaiementDetailFr: formData.escomptePaiementDetailFr,
      nomEmplacement: formData.nomEmplacement,
      accessCodeMEV: formData.accessCodeMEV,
      nomExploitant: formData.nomExploitant,
      PST: formData.PST,
      GST_HST: formData.GST_HST,
    };

    handleNewCompany(dataCompany);
  };

  const handleNewCompany = async (dataCompany) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/company/CreateCompany`,
        {
          method: "POST",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${userState.token}`,
          },
          body: JSON.stringify(dataCompany),
        }
      );

      if (!response.ok) {
        const text = await response.text();
        throw new Error(`Erreur ${response.status} : ${text}`);
      }

      setShowSuccess(true);
      const result = await response.json();
      console.log("Succès :", result);
    } catch (error) {
      console.error("Erreur lors de la création :", error.message);
    }
  };

  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)}>
        {/* ================= GENERAL INFORMATION ================= */}
        <Paper elevation={0} sx={{ p: 3, mb: 3 }}>
          <Typography
            variant="h6"
            sx={{ mb: 3, color: "primary.main", fontWeight: "bold" }}
          >
            {t("GeneralInformation")}
          </Typography>
          <Divider sx={{ mb: 3 }} />

          <Box sx={{ px: 2 }}>
            <Grid container spacing={3}>
              {/* Store Number */}
              <Grid item xs={12} sm={3}>
                <InputLabel sx={{ fontWeight: 700 }}>
                  {t("StoreNumber")}
                </InputLabel>
              </Grid>
              <Grid item xs={12} sm={3}>
                <Controller
                  name="NoAccpac"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      size="small"
                      type="number"
                      label={t("StoreNumber")}
                      onChange={(e) =>
                        field.onChange(Number(e.target.value))
                      }
                    />
                  )}
                />
              </Grid>

              {/* Company Name */}
              <Grid item xs={12} sm={3}>
                <InputLabel sx={{ fontWeight: 700 }}>
                  {t("CompanyName")}
                </InputLabel>
              </Grid>
              <Grid item xs={12} sm={3}>
                <Controller
                  name="nom"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      size="small"
                      label={t("CompanyName")}
                    />
                  )}
                />
              </Grid>

              {/* Business Name */}
              <Grid item xs={12} sm={3}>
                <InputLabel sx={{ fontWeight: 700 }}>
                  {t("BusinessName")}
                </InputLabel>
              </Grid>
              <Grid item xs={12} sm={3}>
                <Controller
                  name="businessName"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      size="small"
                      label={t("BusinessName")}
                    />
                  )}
                />
              </Grid>

              {/* Open Date */}
              <Grid item xs={12} sm={3}>
                <InputLabel sx={{ fontWeight: 700 }}>
                  {t("OpenDate")}
                </InputLabel>
              </Grid>
              <Grid item xs={12} sm={3}>
                <Controller
                  name="dateOuverture"
                  control={control}
                  render={({ field: { onChange, value } }) => (
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <DatePicker
                        label={t("OpenDate")}
                        value={value}
                        onChange={(newValue) =>
                          onChange(
                            newValue
                              ? newValue.format("YYYY-MM-DD")
                              : null
                          )
                        }
                        slotProps={{
                          textField: {
                            fullWidth: true,
                            size: "small",
                          },
                        }}
                      />
                    </LocalizationProvider>
                  )}
                />
              </Grid>
            </Grid>
          </Box>
        </Paper>

        {/* ================= SHAREHOLDERS ================= */}
        <Paper elevation={0} sx={{ p: 3 }}>
          <Typography
            variant="h6"
            sx={{ mb: 3, color: "primary.main", fontWeight: "bold" }}
          >
            {t("Shareholders")}
          </Typography>
          <Divider sx={{ mb: 3 }} />

          <Box sx={{ px: 2 }}>
            <CreateShareholder
              control={control}
              name="shareholders"
              setShareholders={setShareholders}
            />
          </Box>
        </Paper>

        {/* ================= SUBMIT ================= */}
        <Box sx={{ display: "flex", justifyContent: "flex-end", m: 2 }}>
          <Button
            type="submit"
            variant="contained"
            size="large"
            sx={{ minWidth: 200, fontWeight: "bold" }}
          >
            {t("CreateCompany")}
          </Button>
        </Box>
      </form>

      {/* SUCCESS MESSAGE */}
      <Snackbar
        open={showSuccess}
        autoHideDuration={4000}
        onClose={() => setShowSuccess(false)}
      >
        <Alert severity="success" sx={{ width: "100%" }}>
          Company created successfully!
        </Alert>
      </Snackbar>
    </>
  );
}

export default CreateCompany;
